import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Send, Loader2, CheckCircle2, RotateCcw,
  MessageCircleQuestion, GraduationCap, Building2, Newspaper,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "question", label: "Question générale", desc: "Formations, campus, vie étudiante", Icon: MessageCircleQuestion },
  { id: "candidature", label: "Candidature", desc: "Informations avant de postuler", Icon: GraduationCap },
  { id: "entreprise", label: "Partenariat entreprise", desc: "Recrutement, projets, événements", Icon: Building2 },
  { id: "presse", label: "Presse & média", desc: "Interview, communiqué, reportage", Icon: Newspaper },
];

const STEPS = ["Demande", "Vous", "Message", "Envoi"];

const TypeCard = ({ t, selected, onClick }) => {
  const { Icon } = t;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`contact-type-${t.id}`}
      className={`text-left p-5 border group transition-all ${
        selected
          ? "border-navy-500 bg-navy-50"
          : "border-navy-100 bg-white hover:border-navy-500/60 hover:bg-navy-50/30"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-2.5 border transition-colors ${
          selected ? "border-navy-500 bg-white" : "border-navy-100 bg-[#F7F6F2] group-hover:border-navy-500/40"
        }`}>
          <Icon className={`h-5 w-5 ${selected ? "text-navy-600" : "text-navy-700"}`} strokeWidth={1.6} />
        </div>
        <div className="flex-1">
          <div className="font-display text-navy-700 text-lg leading-tight">{t.label}</div>
          <div className="text-[#666] text-xs mt-1">{t.desc}</div>
        </div>
        <div className={`w-4 h-4 border-2 shrink-0 mt-1 ${
          selected ? "border-navy-500 bg-navy-500" : "border-navy-200"
        }`} />
      </div>
    </button>
  );
};

const FieldInput = ({ id, label, required, type = "text", ...rest }) => (
  <div>
    <Label htmlFor={id} className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">
      {label} {required && <span className="text-navy-500">*</span>}
    </Label>
    <Input
      id={id}
      type={type}
      className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500 bg-transparent"
      {...rest}
    />
  </div>
);

export const ContactMultiStepForm = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    type: "",
    name: "",
    email: "",
    phone: "",
    organization: "",
    subject: "",
    message: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const validateStep = () => {
    if (step === 0) return !!data.type;
    if (step === 1) return data.name.trim().length > 1 && /.+@.+\..+/.test(data.email);
    if (step === 2) return data.subject.trim().length > 1 && data.message.trim().length > 4;
    return true;
  };

  const next = () => {
    if (!validateStep()) {
      toast.error("Merci de compléter les champs requis.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        email: data.email,
        subject: data.subject,
        message: data.message,
        type: data.type,
        phone: data.phone || null,
        organization: data.organization || null,
      };
      await axios.post(`${API}/contact`, payload);
      setStep(3);
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail?.[0]?.msg || "Veuillez réessayer." });
    } finally { setLoading(false); }
  };

  const reset = () => {
    setData({ type: "", name: "", email: "", phone: "", organization: "", subject: "", message: "" });
    setStep(0);
  };

  const progress = ((step + (step === 3 ? 0 : 1)) / STEPS.length) * 100;

  return (
    <div data-testid="contact-multistep-form" className="bg-white border border-navy-100 p-6 md:p-10">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-2 mb-3">
          {STEPS.map((s, i) => {
            const isActive = i === step;
            const isDone = i < step || step === 3;
            return (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div
                  className={`shrink-0 w-7 h-7 flex items-center justify-center text-[11px] font-semibold transition-all ${
                    isDone ? "bg-navy-500 text-white"
                      : isActive ? "bg-navy-700 text-white"
                        : "bg-[#F2F2F2] text-[#999]"
                  }`}
                  data-testid={`contact-step-indicator-${i}`}
                >
                  {isDone ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <div className={`text-[10px] uppercase tracking-[0.18em] hidden sm:block ${
                  isActive ? "text-navy-700 font-semibold" : "text-[#999]"
                }`}>
                  {s}
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${isDone ? "bg-navy-500" : "bg-navy-100"}`} />}
              </div>
            );
          })}
        </div>
        <div className="h-[2px] bg-[#F2F2F2] overflow-hidden">
          <motion.div
            className="h-full bg-navy-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
      </div>

      {/* Steps */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.35 }}
          className="min-h-[340px] flex flex-col"
        >
          {step === 0 && (
            <>
              <div className="overline mb-3">Étape 1 / 3</div>
              <h3 className="font-display text-2xl md:text-3xl text-navy-700 leading-tight mb-6">
                De quoi s'agit-il ?
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {TYPES.map((t) => (
                  <TypeCard key={t.id} t={t} selected={data.type === t.id} onClick={() => { set("type", t.id); setTimeout(next, 180); }} />
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="overline mb-3">Étape 2 / 3</div>
              <h3 className="font-display text-2xl md:text-3xl text-navy-700 leading-tight mb-6">
                Faisons connaissance.
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <FieldInput
                  id="ms-name" label="Nom complet" required
                  value={data.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Prénom Nom"
                  data-testid="contact-ms-name"
                />
                <FieldInput
                  id="ms-email" label="Email" type="email" required
                  value={data.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="vous@exemple.com"
                  data-testid="contact-ms-email"
                />
                <FieldInput
                  id="ms-phone" label="Téléphone"
                  value={data.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+225 ..."
                  data-testid="contact-ms-phone"
                />
                {(data.type === "entreprise" || data.type === "presse") && (
                  <FieldInput
                    id="ms-org" label={data.type === "entreprise" ? "Entreprise" : "Média"}
                    value={data.organization}
                    onChange={(e) => set("organization", e.target.value)}
                    placeholder={data.type === "entreprise" ? "Nom de l'entreprise" : "Nom du média"}
                    data-testid="contact-ms-org"
                  />
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="overline mb-3">Étape 3 / 3</div>
              <h3 className="font-display text-2xl md:text-3xl text-navy-700 leading-tight mb-6">
                Votre message.
              </h3>
              <div className="space-y-6">
                <FieldInput
                  id="ms-subject" label="Sujet" required
                  value={data.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  placeholder="En une phrase…"
                  data-testid="contact-ms-subject"
                />
                <div>
                  <Label htmlFor="ms-message" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">
                    Message <span className="text-navy-500">*</span>
                  </Label>
                  <Textarea
                    id="ms-message"
                    rows={6}
                    value={data.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Dites-nous tout…"
                    className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500 resize-none"
                    data-testid="contact-ms-message"
                  />
                  <div className="mt-1 text-right text-[11px] text-[#999]">{data.message.length} caractères</div>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8" data-testid="contact-ms-success">
              <motion.div
                initial={{ scale: 0, rotate: -90 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 180, damping: 14 }}
                className="w-16 h-16 bg-navy-500 text-white flex items-center justify-center mb-6"
              >
                <CheckCircle2 className="h-8 w-8" strokeWidth={1.8} />
              </motion.div>
              <div className="overline text-navy-500 mb-3">Message envoyé</div>
              <h3 className="font-display text-3xl md:text-4xl text-navy-700 leading-tight mb-4">
                Merci{data.name ? `, ${data.name.split(" ")[0]}` : ""}.
              </h3>
              <p className="text-[#555] max-w-md leading-relaxed mb-8">
                Nous avons bien reçu votre demande. Notre équipe vous répondra
                à <span className="text-navy-700 font-semibold">{data.email}</span> en moins de 48 heures.
              </p>
              <button onClick={reset} className="btn-outline-navy" data-testid="contact-ms-restart">
                <RotateCcw className="h-4 w-4" />
                Envoyer un autre message
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      {step < 3 && (
        <div className="mt-8 pt-6 border-t border-navy-100 flex items-center justify-between gap-3">
          <button
            onClick={back}
            disabled={step === 0}
            className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors ${
              step === 0 ? "text-[#CCC] cursor-not-allowed" : "text-navy-700 hover:text-navy-500"
            }`}
            data-testid="contact-ms-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>

          {step < 2 && (
            <button onClick={next} className="btn-red" data-testid="contact-ms-next">
              Continuer
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
          {step === 2 && (
            <button
              onClick={submit}
              disabled={loading}
              className="btn-red disabled:opacity-60"
              data-testid="contact-ms-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Envoi…" : "Envoyer"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactMultiStepForm;
